import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

/**
 * Two revisions of one process, diffed and optionally verified against git, plus the contract
 * validation a person can run on demand against a single revision.
 *
 * Declared here rather than in `common`, matching `script-assets.api.tsx`: nothing outside the
 * Revisions tab consumes these types yet, and adding a shared type forces a frontend rebuild gate
 * for no benefit.
 */

export type PathChange = "added" | "removed" | "modified";

export type PathDiff = {
  path: string;
  change: PathChange;
  /** Unified diff text; absent exactly when `omitted` is set. */
  diff?: string;
  omitted?: {
    reason: "over-path-cap" | "over-comparison-cap";
    bytes: number;
    cap: number;
  };
};

export type LayoutDiff = {
  paths: PathDiff[];
  changed: boolean;
  totalDiffBytes: number;
};

export type DivergenceVerdict = {
  kind:
    | "match"
    | "generator-changed"
    | "content-changed"
    | "impossible"
    | "not-verified";
  severity: "info" | "warn" | "error";
  message: string;
};

export type RevisionVerification = {
  verdict: DivergenceVerdict;
  /** The second view: present only when the verdict is content-changed or impossible. */
  diff?: LayoutDiff;
};

export type RevisionDivergenceReport = {
  a: RevisionVerification;
  b: RevisionVerification;
};

export type ValidationFinding = {
  check: string;
  severity: "error" | "info";
  role?: string;
  portId?: string;
  message: string;
};

export type ProcessValidationResult = {
  result: "passed" | "failed";
  contentHash: string;
  findings: ValidationFinding[];
};

const compareKey = (
  processId: number,
  a: number,
  b: number
): (string | number)[] => [
  "/api/v1/process",
  processId,
  "revisions",
  "compare",
  a,
  b,
];

const verifyKey = (
  processId: number,
  a: number,
  b: number
): (string | number)[] => [
  "/api/v1/process",
  processId,
  "revisions",
  "compare",
  "verify",
  a,
  b,
];

/**
 * Database only comparison of two revisions, per spec's decision that both sides render from the
 * database rather than from a repository. Fast, and it is what the Revisions tab loads first.
 */
export const useCompareRevisions = (
  processId: number | undefined,
  revisionAId: number | undefined,
  revisionBId: number | undefined
): UseQueryResult<LayoutDiff, Error> =>
  useQuery<LayoutDiff, Error>({
    queryKey: compareKey(processId ?? 0, revisionAId ?? 0, revisionBId ?? 0),
    enabled:
      undefined !== processId &&
      undefined !== revisionAId &&
      undefined !== revisionBId,
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/process/${processId}/revisions/compare`,
        { params: { a: revisionAId, b: revisionBId } }
      );
      return res.data as LayoutDiff;
    },
  });

/**
 * The optional, later half of the same feature: checks each revision's rendered-today content
 * against what its tag actually committed. Never blocks the comparison above, which is why this
 * is its own query rather than a field the compare call waits on. A slow or unreachable remote
 * degrades to a `not-verified` verdict per side on the server; this hook never retries either, so
 * a genuinely offline remote resolves to that same state quickly rather than spinning.
 */
export const useVerifyRevisions = (
  processId: number | undefined,
  revisionAId: number | undefined,
  revisionBId: number | undefined
): UseQueryResult<RevisionDivergenceReport, Error> =>
  useQuery<RevisionDivergenceReport, Error>({
    queryKey: verifyKey(processId ?? 0, revisionAId ?? 0, revisionBId ?? 0),
    enabled:
      undefined !== processId &&
      undefined !== revisionAId &&
      undefined !== revisionBId,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/process/${processId}/revisions/compare/verify`,
        { params: { a: revisionAId, b: revisionBId } }
      );
      return res.data as RevisionDivergenceReport;
    },
  });

/**
 * Validates one revision's contract against itself and records the result as evidence
 * (`process_revision_validation`). Triggered per revision, on demand, rather than fetched for
 * every row on load: evidence is append only, so an automatic call per row would write a new
 * evidence row every time the tab opened.
 *
 * The revision to validate is the mutation variable rather than baked into the hook, so one
 * instance serves every row in the table.
 */
export const useValidateProcessRevision = (): UseMutationResult<
  ProcessValidationResult,
  ErrorNotification,
  number
> => {
  const { setNotification } = useNotifications();

  return useMutation<ProcessValidationResult, ErrorNotification, number>({
    mutationFn: async (revisionId: number) => {
      const res = await axios.post(`/api/v1/process/${revisionId}/validate`);
      return res.data as ProcessValidationResult;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "process-revision-validate-error",
        message: "Validation could not be run",
        details:
          error?.response?.data?.message ??
          "Check that you have permission to edit this revision.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
